
// ======================
// CONFIG
// ======================
export const BOARD_SIZE = 12;
export const MAX_POOL = 14;

export const GAME_LEVELS = {
  beginner: {
    targetLines: 8,
    hintPercentage: 0.9,
    maxVal: 10,
    extraPool: 1,
  },
  intermediate: {
    targetLines: 11,
    hintPercentage: 0.8,
    maxVal: 20,
    extraPool: 3,
  },
  advanced: {
    targetLines: 14,
    hintPercentage: 0.6,
    maxVal: 30,
    extraPool: 5,
  },
};

// ======================
// RANDOM HELPERS
// ======================
export const rand = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

// ======================
// EQUATION GENERATOR
// ======================
export const randomOp = (gameType) => {
  if (gameType === "puzzle_2") {
    return Math.random() > 0.5 ? "x" : "/";
  }
  return Math.random() > 0.5 ? "+" : "-";
};

export const makeEquation = (
  gameType,
  left = null,
  right = null,
  result = null,
  maxVal = 20
) => {
  const minVal = gameType === "puzzle_2" ? 2 : 1;

  for (let i = 0; i < 100; i++) {
    const L = left ?? rand(minVal, maxVal);
    const R = right ?? rand(minVal, maxVal);
    const op = randomOp(gameType);

    let res;

    if (op === "+") res = L + R;
    if (op === "-") {
      if (L <= R) continue;
      res = L - R;
    }
    if (op === "x") {
      res = L * R;
      if (res > 200) continue;
    }
    if (op === "/") {
      if (R === 0 || L % R !== 0) continue;
      res = L / R;
    }

    if (res <= 0) continue;
    if (result != null && result !== res) continue;

    return [String(L), op, String(R), "=", String(res)];
  }

  const L = left ?? 1;
  const R = right ?? 1;
  return [
    String(L),
    gameType === "puzzle_2" ? "x" : "+",
    String(R),
    "=",
    String(gameType === "puzzle_2" ? L * R : L + R),
  ];
};

// ======================
// BOARD HELPERS
// ======================
export const createBoard = (size) =>
  Array.from({ length: size }, () => Array(size).fill(""));

// ======================
// PLACEMENT CHECKS
// ======================
export const inBounds = (r, c, size) =>
  r >= 0 && r < size && c >= 0 && c < size;

export const canPlace = (board, tokens, r, c, dir, size) => {
  for (let i = 0; i < tokens.length; i++) {
    const rr = dir === "across" ? r : r + i;
    const cc = dir === "across" ? c + i : c;

    if (!inBounds(rr, cc, size)) return false;

    const existing = board[rr][cc];
    if (existing && existing !== tokens[i]) return false;
  }
  return true;
};

export const place = (board, tokens, r, c, dir) => {
  const newBoard = board.map((row) => [...row]);

  for (let i = 0; i < tokens.length; i++) {
    const rr = dir === "across" ? r : r + i;
    const cc = dir === "across" ? c + i : c;
    newBoard[rr][cc] = tokens[i];
  }

  return newBoard;
};

// ======================
// PUZZLE GENERATION
// ======================
export const generatePuzzle = (level, gameType = "puzzle_1") => {
  const config = GAME_LEVELS[level];

  let board = createBoard(BOARD_SIZE);
  let lines = [];

  const centerR = Math.floor(BOARD_SIZE / 2);
  const centerC = Math.floor(BOARD_SIZE / 2) - 2;

  // seed equation
  const seed = makeEquation(
    gameType,
    null,
    null,
    null,
    config.maxVal
  );

  board = place(board, seed, centerR, centerC, "across");
  lines.push({ tokens: seed, r: centerR, c: centerC, dir: "across" });

  let attempts = 0;

  while (lines.length < config.targetLines + 1 && attempts < 200) {
    attempts++;

    const base = lines[Math.floor(Math.random() * lines.length)];
    const idxOptions = [0, 2, 4];
    const intersectIdx =
      idxOptions[Math.floor(Math.random() * idxOptions.length)];

    const baseR =
      base.dir === "down" ? base.r + intersectIdx : base.r;
    const baseC =
      base.dir === "across" ? base.c + intersectIdx : base.c;

    const shared = board[baseR][baseC];
    const sharedVal = Number(shared);
    if (!sharedVal) continue;

    const newDir = base.dir === "across" ? "down" : "across";
    const newIdx =
      idxOptions[Math.floor(Math.random() * idxOptions.length)];

    let tokens;
    if (newIdx === 0)
      tokens = makeEquation(gameType, sharedVal);
    else if (newIdx === 2)
      tokens = makeEquation(gameType, null, sharedVal);
    else
      tokens = makeEquation(gameType, null, null, sharedVal);

    const startR = newDir === "across" ? baseR : baseR - newIdx;
    const startC = newDir === "across" ? baseC - newIdx : baseC;

    if (!canPlace(board, tokens, startR, startC, newDir, BOARD_SIZE))
      continue;

    board = place(board, tokens, startR, startC, newDir);
    lines.push({ tokens, r: startR, c: startC, dir: newDir });
  }

  return { board, lines };
};

export const generateHiddenSlots = (
  lines,
  hintPercentage = 0.5
) => {
  const hiddenSlots = [];
  const usedCoords = new Set();

  lines.forEach((line) => {
    const numberIndexes = [0, 2, 4];

    // минимум 1 вопрос в каждом уравнении
    const hiddenIndex =
      numberIndexes[
        Math.floor(Math.random() * numberIndexes.length)
      ];

    const r =
      line.dir === "across"
        ? line.r
        : line.r + hiddenIndex;

    const c =
      line.dir === "across"
        ? line.c + hiddenIndex
        : line.c;

    const key = `${r}-${c}`;

    if (!usedCoords.has(key)) {
      usedCoords.add(key);

      hiddenSlots.push({
        key,
        value: Number(line.tokens[hiddenIndex]),
      });
    }
  });

  return hiddenSlots.slice(0, 14);
};

// ======================
// POOL BUILDER
// ======================
export const buildPoolValues = (
  hiddenSlots,
  level
) => {
  const config = GAME_LEVELS[level];

  const required = hiddenSlots.map(
    (slot) => slot.value
  );

  let pool = [...required];

  const used = new Set(pool);

  while (pool.length < MAX_POOL) {
    const n = rand(1, config.maxVal * 2);

    pool.push(n);

    used.add(n);
  }

  return shuffle(pool).slice(0, MAX_POOL);
};
// ======================
// VALIDATION
// ======================
export const validatePuzzle = (lines) => {
  return lines.every((line) => {
    const [L, op, R, , res] = line.tokens.map(Number);

    if (op === "+") return L + R === res;
    if (op === "-") return L - R === res;
    if (op === "x") return L * R === res;
    if (op === "/") return R !== 0 && L / R === res;

    return false;
  });
};
